import store from 'redux/store';
import { isEmpty, isString } from 'lodash-es';
import i18n from 'services/i18n';
import { getDynamicStateData } from 'translations/dynamic';
import {
  hideModal,
  showModal,
  updateModalGeneralProps,
  updateModalPrimaryButtonProps,
  updateModalSecondaryButtonProps
} from 'redux/actions/global';
import { deleteCard } from 'redux/actions/card';
import { FIELDS } from 'components/containers/Hosted/consts';
import { testRegex, trackDeleteCard, parseE164ToPhoneNumber } from 'utilities';
import { getFieldBooleanValue, buildStringWithCommasByOrder } from 'utilities/global';
import { constructE164 } from 'utilities/customer';
import MODAL_TYPES from 'components/containers/Modal/Modals/types';
import {
  MANDATORY_CARD_FIELDS,
  ADDRESS_FIELDS,
  CARD_BRANDS,
  BILLING_ADDRESS_COLLECT_TYPES,
  BILLING_ADDRESS_COLLECT_VALUES,
  KORTA_PAYMENT_METHOD_TYPE_NAME,
  VALITOR_PAYMENT_METHOD_TYPE_NAME,
  EXPANDED_BIN_LENGTH,
  BIN_LENGTH
} from 'consts';
import {
  CUSTOMER_ADDRESS_FIELDS,
  CUSTOMER_AFT_FIELDS,
  PHONE_NUMBER_FIELD,
  CUSTOMER_FIELDS
} from 'consts/customer';

// TODO - redundant when finish redesign - until line 51
export const isCardNumberValid = (cardNumber, regex = '') => {
  const cardNumberAsString = !isString(cardNumber) ? '' : cardNumber;
  const cardNumberWithoutMasking = cardNumberAsString
    .replace(/\s/g, '')
    .replace(/-/g, '');

  if (cardNumberWithoutMasking.length < 7) {
    return false;
  }

  if (regex && !testRegex({ regex, value: cardNumberWithoutMasking })) return false;

  const { type: cardBrandType } = parseCardBrand(cardNumberWithoutMasking);
  return (
    Object.values(CARD_BRANDS).find(brandObj => brandObj.type === cardBrandType)
      ?.skip_luhn || luhnCheck(cardNumberWithoutMasking)
  );
};

const luhnCheck = cardNumber => {
  const reversedCardNumber = cardNumber.split('').reverse().join('');
  let t = '';
  let n = 0;

  for (let i = 0; i < reversedCardNumber.length; i++) {
    let c = parseInt(reversedCardNumber.charAt(i));
    if (i % 2 !== 0) c *= 2;
    t += c;
  }

  for (let i = 0; i < t.length; i++) {
    const c = parseInt(t.charAt(i));
    n += c;
  }

  return n !== 0 && n % 10 === 0;
};
//

const initField = ({ value = '', touched = false } = {}) => ({
  value,
  error: '',
  touched
});

export const initForm = ({
  saveCardDefault,
  // TODO: delete or add
  // customerAddress,
  // geoCountry,
  cardHolderName
  // pageCountry
}) => {
  const base = {};

  const mandatory = {
    [MANDATORY_CARD_FIELDS.number]: initField(),
    [MANDATORY_CARD_FIELDS.cvv]: initField(),
    [MANDATORY_CARD_FIELDS.name]: initField({ value: cardHolderName }),
    [MANDATORY_CARD_FIELDS.expiration]: initField()
  };

  // TODO: edit or delete
  // base.address = getInitFormAddress({ customerAddress, pageCountry, geoCountry });
  base.mandatory = mandatory;
  base.saveForFuture = saveCardDefault || false;

  return base;
};

export const initEditedForm = selectedCard => {
  const {
    number,
    cvv,
    name,
    expiration_month: expirationMonth,
    expiration_year: expirationYear,
    address
  } = selectedCard;

  const streetName = buildStringWithCommasByOrder(address, [
    'line_1',
    'line_2',
    'line_3'
  ]);
  const country = selectedCard.address?.country ?? '';
  const city = selectedCard.address?.city ?? '';
  const state = selectedCard.address?.state ?? '';
  const zip = selectedCard.address?.zip ?? '';

  const base = {};
  const mandatory = {
    [MANDATORY_CARD_FIELDS.number]: initField({ value: number }),
    [MANDATORY_CARD_FIELDS.cvv]: initField({ value: cvv }),
    [MANDATORY_CARD_FIELDS.name]: initField({ value: name }),
    [MANDATORY_CARD_FIELDS.expiration]: initField({
      value: `${expirationMonth}/${expirationYear}`
    })
  };
  const addressFields = {
    [ADDRESS_FIELDS.country]: initField({ value: country }),
    [ADDRESS_FIELDS.city]: initField({ value: city }),
    [ADDRESS_FIELDS.streetName]: initField({ value: streetName }),
    [ADDRESS_FIELDS.state]: initField({ value: state }),
    [ADDRESS_FIELDS.zip]: initField({ value: zip })
  };

  base.mandatory = mandatory;
  base.address = addressFields;
  base.saveForFuture = true;

  return base;
};

export const parsedCardToApi = ({
  formValues,
  type,
  isEdited,
  isRedirect,
  withBillingAddress
}) => {
  const cardFields = parseCardFields({ formValues, isEdited });

  const address = withBillingAddress
    ? {
        name: cardFields?.name,
        country: formValues?.billingAddress?.[CUSTOMER_ADDRESS_FIELDS.country],
        city: formValues?.billingAddress?.[CUSTOMER_ADDRESS_FIELDS.city],
        zip: formValues?.billingAddress?.[CUSTOMER_ADDRESS_FIELDS.zip],
        line_1: formValues?.billingAddress?.[CUSTOMER_ADDRESS_FIELDS.line_1],
        ...(formValues?.billingAddress?.[CUSTOMER_ADDRESS_FIELDS.state] && {
          state: formValues?.billingAddress?.[CUSTOMER_ADDRESS_FIELDS.state]
        })
      }
    : null;

  return {
    address,
    fields: isRedirect ? {} : cardFields,
    metadata: {},
    type
  };
};

export const parseCardFields = ({ formValues, isEdited = false }) => {
  const [MM, YY] =
    formValues?.[MANDATORY_CARD_FIELDS.expiration]?.replace(/\s/g, '').split('/') || [];
  const number = formValues?.[MANDATORY_CARD_FIELDS.number]?.replace(/\s/g, '');

  return {
    name: formValues?.[MANDATORY_CARD_FIELDS.cardholder],
    ...(!isEdited && { cvv: formValues?.[MANDATORY_CARD_FIELDS.cvv] }),
    ...(!isEdited && { number }),
    expiration_month: MM,
    expiration_year: YY
  };
};

export const formatCardDataToApi = ({
  requiredFields,
  paymentMethodType,
  isEdited,
  isRedirect
}) => {
  const { mandatory, address } = requiredFields || {};
  const getFieldValue = field => field.value;
  const data = {
    address: isEmpty(address)
      ? null
      : {
          city: getFieldValue(address.city),
          country: getFieldValue(address.country),
          ...(address.state &&
            getFieldValue(address.state) && { state: getFieldValue(address.state) }),
          zip: getFieldValue(address.zip),
          name: getFieldValue(mandatory.name),
          line_1: getFieldValue(address.streetName)
        },
    fields: {
      ...(!isEdited && {
        number: getFieldValue(mandatory.number)
          ? getFieldValue(mandatory.number).replace(/ /g, '')
          : ''
      }),
      ...(!isEdited && { cvv: getFieldValue(mandatory.cvv) }),
      expiration_month: getFieldValue(mandatory.expiration).substring(0, 2),
      expiration_year: getFieldValue(mandatory.expiration).substr(-2),
      name: getFieldValue(mandatory.name)
    },
    metadata: {},
    type: paymentMethodType
  };

  if (isRedirect) {
    data.fields = {};
  }

  return data;
};

export const formatCardToApiTokenization = ({
  paymentMethodType,
  customer,
  cardFields,
  addressFields,
  additionalFields,
  withAddress
}) => {
  const { expiration, number, ...restCardFields } = cardFields;
  const { streetName, ...restOfAddressFields } = addressFields;

  const formattedAddressFields = withAddress
    ? {
        ...restOfAddressFields,
        line_1: streetName,
        name: cardFields[MANDATORY_CARD_FIELDS.name]
      }
    : undefined;

  const data = {
    type: paymentMethodType,
    customer,
    fields: {
      expiration_month: expiration.substring(0, 2),
      expiration_year: expiration.substr(-2),
      number: number.replace(/\s/g, ''),
      ...restCardFields,
      ...additionalFields
    },
    ...(withAddress && { address: formattedAddressFields })
  };

  return data;
};

export const formatAdditionalData = fields => {
  const additionalFields = {};
  const paymentMethodOptions = {};

  Object.keys(fields).forEach(fieldName => {
    const { location, type, value: fieldValue } = fields[fieldName];
    let value;
    if (type === 'boolean') {
      value = getFieldBooleanValue(fieldValue);
    } else {
      value = fieldValue;
    }
    if (location === 'fields') {
      additionalFields[fieldName] = value;
    }
    if (location === 'payment_method_options') {
      paymentMethodOptions[fieldName] = value;
    }
  });

  return {
    additionalFieldsParsed: additionalFields,
    paymentMethodOptionsParsed: paymentMethodOptions
  };
};

export const showTypesDropdownCompCheck = ({ paymentMethodTypes = [] }) => {
  if (paymentMethodTypes.length !== 1) return true;

  const kortaAndValitorPaymentMethodType = [
    KORTA_PAYMENT_METHOD_TYPE_NAME,
    VALITOR_PAYMENT_METHOD_TYPE_NAME
  ];
  const { type } = paymentMethodTypes[0] ?? {};

  if (kortaAndValitorPaymentMethodType.includes(type)) {
    return false;
  }

  return true;
};

export const clearNumber = (value = '') => {
  return value.replace(/\D+/g, '');
};

export const getFormatCreditCardNumber = value => {
  if (!value) return value;

  const clearValue = clearNumber(value);
  const nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(
    4,
    8
  )} ${clearValue.slice(8, 12)} ${clearValue.slice(12, 16)} ${clearValue.slice(16, 19)} `;

  return nextValue.trim();
};

export const parseExpirationDate = value => {
  const prevExpiry = value.split(' / ').join('/');

  if (!prevExpiry) return null;
  let expiry = prevExpiry;
  if (/^[2-9]$/.test(expiry)) {
    expiry = `0${expiry}`;
  }

  if (prevExpiry.length === 2 && +prevExpiry > 12) {
    const [head, ...tail] = prevExpiry.split('');
    expiry = `0${head}/${tail.join('')}`;
  }

  if (/^1[/-]$/.test(expiry)) {
    return '01 / ';
  }

  expiry = expiry.match(/(\d{1,2})/g) || [];
  if (expiry.length === 1) {
    if (prevExpiry.includes('/')) {
      return expiry[0];
    }
    if (/\d{2}/.test(expiry)) {
      return `${expiry[0]} / `;
    }
  }
  if (expiry.length > 2) {
    const [, month = null, year = null] =
      expiry.join('').match(/^(\d{2}).*(\d{2})$/) || [];
    return [month, year].join(' / ');
  }
  return expiry.join(' / ');
};

export const parseCvv = value => {
  const clearValue = clearNumber(value);
  const maxLength = 4;

  return clearValue.slice(0, maxLength);
};

export const parseCardBrand = number => {
  const bestMatch = { type: null, brand: null, score: null };

  if (!number) return bestMatch;

  const value = String(number).replace(/\s/g, '');

  Object.values(CARD_BRANDS).forEach(({ type, name, patterns }) => {
    patterns.forEach(patternAsNumber => {
      const p = `${patternAsNumber}`;
      if (value.substr(0, p.length) === p) {
        const matchedStringScore = p.length;

        if (matchedStringScore > bestMatch.score) {
          bestMatch.score = matchedStringScore;
          bestMatch.brand = name;
          bestMatch.type = type;
        }
      }
    });
  });

  return bestMatch;
};

export const showBillingAddress = (shouldShow, customer) => {
  if (isEmpty(customer)) return false;

  let parsedShouldShow = false;

  if (typeof shouldShow === 'boolean') {
    parsedShouldShow = shouldShow;
  } else if (
    typeof shouldShow === 'string' &&
    Object.values(BILLING_ADDRESS_COLLECT_TYPES).includes(shouldShow)
  ) {
    parsedShouldShow = BILLING_ADDRESS_COLLECT_VALUES[shouldShow];
  }

  return parsedShouldShow;
};

export const getDynamicStateLabel = (customerAddresses, country) => {
  const [customerAddress] = customerAddresses || [];
  const { state } = customerAddress || {};

  return getDynamicStateData(state, country)?.label;
};

// IOS scan
export const getFormattedExpirationValueIOSScan = value => {
  // expiration value when being scanned in ios camera is in the following format: X.XXXX or XX.XXXX
  const [month, year] = value.includes('.') ? value.split('.') : value.split('/');
  const parsedValue = `${month.length === 1 ? `0${month}` : month} / ${
    year ? year.slice(2) : ''
  }`;
  return parsedValue;
};

// Google’s autocomplete
export const getFormattedExpirationValue = inputValue => {
  const value = inputValue.replace(/\s/g, '');

  // MM/YYYY
  if (value.length === 7) return `${value.substring(0, 2)} / ${value.slice(-2)}`;
  // MM/YY
  if (value.length === 5) return `${value.substring(0, 2)} / ${value.slice(3, 5)}`;

  return inputValue;
};

export const splitExpirationDateToMMYY = (expiration = '') => {
  let expMonth = '';
  let expYear = '';

  if (expiration && isString(expiration)) {
    const month = expiration.substring(0, 2).trim();
    const year = expiration.substring(expiration.length - 2).trim();

    if (month.length === 2 && year.length === 2) {
      expMonth = month;
      expYear = year;
    }
  }

  return {
    expMonth,
    expYear
  };
};

export const getCardBrandByMapping = (cardPagePmts = [], selectedType = '') => {
  const cardPmt = cardPagePmts.find(pmt => pmt.type === selectedType);
  let cardBrand = '';

  if (cardPmt?.mapping?.length) {
    cardBrand = cardPmt.mapping[0]?.brand ?? '';
  }

  return cardBrand;
};

export const getCardNumberRegex = ({ pmtFields = [] }) => {
  let cardNumberRegex;

  if (pmtFields?.length) {
    cardNumberRegex = pmtFields.find(field =>
      [MANDATORY_CARD_FIELDS.number, FIELDS.CARD_NUMBER].includes(field.name)
    )?.regex;
  }

  return cardNumberRegex || '';
};

export const getCustomerInitialValues = ({ customer }) => {
  const {
    addresses,
    name,
    phone_number: customerPhoneNumber,
    email,
    business_vat_id: VAT
  } = customer || {};
  const [customerAddress] = addresses || [];

  const { countryCode, number } = parseE164ToPhoneNumber(customerPhoneNumber) || {};
  const formattedNumber =
    countryCode && number
      ? constructE164({
          countryCode,
          number,
          withSign: true
        })
      : null;

  return {
    [CUSTOMER_FIELDS.addresses]: getAddressInitialValues({ customerAddress }),
    [CUSTOMER_FIELDS.name]: name || '',
    [CUSTOMER_FIELDS.email]: email || '',
    [CUSTOMER_FIELDS.business_vat_id]: VAT || '',
    [CUSTOMER_FIELDS.phone]:
      formattedNumber && customerPhoneNumber
        ? {
            [PHONE_NUMBER_FIELD.country]: countryCode || '',
            [PHONE_NUMBER_FIELD.number]: number || '',
            formattedNumber
          }
        : {}
  };
};

export const getAddressInitialValues = ({ customerAddress, geoCountry = '' }) => {
  if (isEmpty(customerAddress)) return {};
  const {
    country,
    city,
    line_1: line1,
    state,
    zip,
    phone_number: customerPhoneNumber,
    name
  } = customerAddress || {};

  const { countryCode, number } = parseE164ToPhoneNumber(customerPhoneNumber) || {};
  const formattedNumber =
    countryCode && number
      ? constructE164({
          countryCode,
          number,
          withSign: true
        })
      : null;

  return {
    [ADDRESS_FIELDS.country]: country || geoCountry,
    [ADDRESS_FIELDS.city]: city || '',
    [ADDRESS_FIELDS.line_1]: line1 || '',
    [ADDRESS_FIELDS.state]: state || '',
    [ADDRESS_FIELDS.zip]: zip || '',
    [CUSTOMER_AFT_FIELDS.name]: name || '',
    [CUSTOMER_AFT_FIELDS.phone_number]:
      formattedNumber && customerPhoneNumber
        ? {
            [PHONE_NUMBER_FIELD.country]: countryCode || '',
            [PHONE_NUMBER_FIELD.number]: number || '',
            formattedNumber
          }
        : {}
  };
};

const deleteCardCallback = (cardToken, externalCallBack) => {
  trackDeleteCard();
  store.dispatch(updateModalPrimaryButtonProps({ isLoading: true }));
  store.dispatch(updateModalSecondaryButtonProps({ disabled: true }));
  store.dispatch(updateModalGeneralProps({ shouldDisableCloseOption: true }));
  store.dispatch(
    deleteCard({
      cardToken,
      closeModal: () => {
        store.dispatch(hideModal());
        externalCallBack();
      }
    })
  );
};

export const handleRemoveCard = (cardToken, externalCallBack = () => {}) => {
  store.dispatch(
    showModal({
      type: MODAL_TYPES.CTA,
      props: {
        generalProps: {
          name: 'cta-modal',
          title: i18n.t('card_tokenization.delete_card_title'),
          subTitle: i18n.t('card_tokenization.delete_card_subtitle'),
          primaryButtonProps: {
            children: `${i18n.t('delete')}`,
            onClick: () => deleteCardCallback(cardToken, externalCallBack)
          },
          secondaryButtonProps: {
            children: `${i18n.t('cancel')}`,
            onClick: () => store.dispatch(hideModal())
          }
        }
      }
    })
  );
};

export const getBinDataObject = bin => {
  const { configData } = store.getState().global || {};
  const { use_expanded_bin: useExpandedBin } = configData || {};

  return useExpandedBin
    ? { card_expanded_bin: bin.substring(0, EXPANDED_BIN_LENGTH) }
    : { card_bin: bin.substring(0, BIN_LENGTH) };
};
